import { functionsUrl } from './firebase';

export interface ExamUploadInitPayload {
  userId: string;
  filePath: string;
  fileName?: string;
  fileSize?: number;
}

export interface ExamUploadInitResult {
  ok: boolean;
  uploadId: string;
}

export interface ExamUploadProcessResult {
  ok: boolean;
  uploadId: string;
  versionId: string;
  pages: number;
  chars: number;
  detectedSections: number;
  status: 'needs_review' | 'failed' | 'processing';
  extractionMode?: 'regex';
}

export interface ExamStructuredExtractionResult {
  ok: boolean;
  versionId: string;
  extractionMode: 'ai' | 'regex-fallback';
  subjects: number;
  confidence: number | null;
  warnings: string[];
  metadata?: any;
  cargos?: any[];
  subjectsList?: any[];
  qualityScore?: { score: number; coverage: number; topicDensity: number; subjects: number; cargos: number };
  qualityGate?: { passed: boolean; reasons: string[]; metrics: { validSubjects: number; withTopics: number; topicCoverage: number } };
}

export interface ExamReviewQueueItem {
  uploadId: string;
  versionId: string;
  fileName?: string;
  createdAt?: any;
  selectedCargo?: string | null;
  confidenceGlobal?: number | null;
  qualityScore?: { score: number; coverage: number; topicDensity: number; subjects: number; cargos: number } | null;
  qualityGate?: { passed: boolean; reasons: string[]; metrics: { validSubjects: number; withTopics: number; topicCoverage: number } } | null;
  telemetryLast?: { step?: string; durationMs?: number; source?: string; fallback?: string | null; ok?: boolean; error?: string | null; meta?: any } | null;
  cargos?: Array<{ name: string; confidence?: string | number | null; subjects?: string[] }>;
  status: 'needs_review' | 'approved' | 'failed' | 'processing' | string;
}

const postJson = async <T>(path: string, payload: unknown, timeoutMs: number = 120000): Promise<T> => {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);

  try {
    const res = await fetch(`${functionsUrl}${path}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
      signal: controller.signal,
    });

    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
      throw new Error(data?.error || `Erro HTTP ${res.status}`);
    }
    return data as T;
  } catch (err: any) {
    if (err?.name === 'AbortError') {
      throw new Error('Tempo limite excedido ao processar o PDF. Tente novamente em instantes.');
    }
    throw err;
  } finally {
    clearTimeout(timer);
  }
};

export const initExamUpload = async (payload: ExamUploadInitPayload) =>
  postJson<ExamUploadInitResult>('/exam-upload/init', payload);

export const processExamUpload = async (uploadId: string) =>
  postJson<ExamUploadProcessResult>('/exam-upload/process', { uploadId });

export const extractStructuredExam = async (payload: { uploadId?: string; versionId?: string }) =>
  postJson<ExamStructuredExtractionResult>('/exam-upload/extract-structured', payload);

export const getExamReviewQueue = async () =>
  postJson<{ ok: boolean; items: ExamReviewQueueItem[] }>('/exam-upload/review-queue', {});

export const approveExamUpload = async (payload: { uploadId: string; approvedCargoNames?: string[] }) =>
  postJson<{ ok: boolean; uploadId: string; versionId: string; status: string }>('/exam-upload/approve', payload);

export const rejectExamUpload = async (payload: { uploadId: string; reason?: string }) =>
  postJson<{ ok: boolean; uploadId: string; versionId: string; status: string }>('/exam-upload/reject', payload);

export const selectExamUploadCargo = async (payload: { uploadId: string; selectedCargo: string }) =>
  postJson<{ ok: boolean; uploadId: string; selectedCargo: string }>('/exam-upload/select-cargo', payload);
